import { Link, useSearchParams } from 'react-router';
import { Button, Card, EmptyState, ErrorState, PageHeader, Spinner } from '@helios/ui';
import { PERMISSIONS } from '@helios/authz';
import { sessionScopeKey, useSessionScopeFence } from '@/hooks/useSessionScope';
import { ProjectMapPanel } from '@/features/realtime/ProjectMapPanel';
import { projectFiltersFromParams } from './projectFilters';
import { portfolioMapParams, portfolioMapReturnHref } from './portfolioMapScope';
import { useProjectPortfolioMap, useProjectsRealtime } from './projects.hooks';

export function PortfolioMapWorkspace() {
  const scope = useSessionScopeFence(PERMISSIONS.PROJECTS_READ);
  const [params] = useSearchParams();
  if (!scope.enabled)
    return (
      <EmptyState
        title="Project map access required"
        description="Current project-read authority and the native map source are required."
      />
    );
  return (
    <PortfolioMapContent
      key={`${JSON.stringify(sessionScopeKey(scope.identity))}:${portfolioMapParams(params)}`}
    />
  );
}

function PortfolioMapContent() {
  const [params] = useSearchParams();
  const filters = projectFiltersFromParams(params);
  const query = useProjectPortfolioMap(filters);
  useProjectsRealtime();
  const data = !query.isFetching && !query.isError ? query.data : undefined;
  const restricted =
    query.error &&
    typeof query.error === 'object' &&
    'status' in query.error &&
    query.error.status === 403;
  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Portfolio Operations"
        title="Project Portfolio · Map"
        description="The same complete filtered Portfolio cohort, independent of register page or sort."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to={portfolioMapReturnHref(params)}>Return to Portfolio</Link>
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              Refresh same scope
            </Button>
          </div>
        }
      />
      {query.isFetching ? (
        <Card className="flex items-center gap-2 p-4">
          <Spinner className="h-4 w-4" />
          Loading the complete Portfolio map cohort…
        </Card>
      ) : query.isError || !data ? (
        <ErrorState
          title={
            restricted ? 'Project map source access required' : 'Portfolio map scope unavailable'
          }
          description={
            query.error instanceof Error
              ? query.error.message
              : 'No complete current map cohort is available. The tenant-wide map is not substituted.'
          }
          action={
            <Button size="sm" onClick={() => void query.refetch()}>
              Retry same scope
            </Button>
          }
        />
      ) : (
        <>
          <Card className="space-y-1 p-3 text-xs" aria-label="Complete Portfolio mapping coverage">
            <p>
              {data.map.markers.length} mapped / {data.scope.includedCount} included projects ·{' '}
              {data.scope.unmappedCount} without recorded coordinates
            </p>
            <p>
              {data.scope.matchingCount} matching register records · {data.scope.excludedCount}{' '}
              hidden from Portfolio
            </p>
            <p className="text-muted-foreground">
              All native business filters, including region identity and chart cohort, are retained.
              Map balances and active alerts describe mapped projects only; recorded models remain
              separate from observed performance.
            </p>
          </Card>
          <ProjectMapPanel
            data={data.map}
            isLoading={false}
            isError={false}
            onRetry={() => void query.refetch()}
            portfolioScope={data.scope}
          />
        </>
      )}
    </div>
  );
}
